import { Injectable } from '@angular/core';
import { BaseColor, Condition } from '@wb/core/enums/inventory';
import { DataOne } from '@wb/core/interfaces/data-one';
import { Inventory, Make, Model } from '@wb/core/interfaces/inventory';
import { Query } from 'apollo-angular';
import gql from 'graphql-tag';

//#region Inventory
export interface InventoriesResponse {
  inventories: {
    code: string;
    success: boolean;
    message: string;
    data: {
      count: number;
      data: [Inventory];
    };
  };
}

export interface InventoryQueryVariables {
  limit?: number;
  offset?: number;
  input?: InventoriesFilter;
}

export interface InventoryFilter {
  uid?: string;
  account_uid?: string;
  stock_id?: string;
  shared_inventory?: number;
  type?: Condition;
  vin?: string;
  year?: number;
  make?: string;
  model?: string;
  options?: string;
  body?: string;
  trim?: string;
  doors?: string;
  image?: string;
  history?: string;
  price_msrp?: number;
  price_advert?: number;
  price_web?: number;
  price_invoice?: number;
  price_variable?: number;
  price_after_incentives?: number;
  price_wholesale?: number;
  mpg?: string;
  mileage?: number;
  exterior_color?: string;
  exterior_color_base?: BaseColor;
  interior_color?: string;
  interior_color_base?: BaseColor;
  drivetrain?: string;
  transmission?: string;
  fuel_type?: string;
  engine_cylinders?: string;
  engine_displacement?: string;
  certified?: number;
  date_in_stock?: string;
  jato_vehicle_id?: string;
  jato_vehicle_id_date?: string;
  image_hash?: string;
  image_source?: string;
  stock_image?: string;
  gross_weight?: number;
  option_codes?: string;
  dealer_id?: string;
  delisted?: number;
  dataone?: DataOne;
  created_at?: string;
  updated_at?: string;
}

export interface InventoriesSearch {
  color?: string;
  condition?: Condition;
  maxPrice?: string;
  minPrice?: string;
  make?: string;
  model?: string;
  maxYear?: string;
  minYear?: string;
  searchString?: string;
}

export interface InventoriesFilter {
  where?: InventoryFilter;
  search?: InventoriesSearch;
}

@Injectable({
  providedIn: 'root',
})
export class InventoriesGQL extends Query<InventoriesResponse, InventoryQueryVariables> {
  document = gql(`
    query inventories ($input: ModelInventoriesFilterInput, $limit: Int, $offset: Int) {
      inventories (input: $input, limit: $limit, offset: $offset) {
        code
        success
        message
        data {
          count
          data {
            uid
            stock_id
            shared_inventory
            type
            vin
            year
            make
            model
            options
            body
            trim
            doors
            image
            history
            price_msrp
            price_advert
            price_web
            price_invoice
            price_variable
            price_after_incentives
            price_wholesale
            mpg
            mileage
            exterior_color
            exterior_color_base
            interior_color
            interior_color_base
            drivetrain
            transmission
            fuel_type
            engine_cylinders
            engine_displacement
            certified
            date_in_stock
            jato_vehicle_id
            jato_vehicle_id_date
            image_hash
            image_source
            stock_image
            gross_weight
            option_codes
            dealer_id
            marketscan_id
            delisted
            noPrice
            created_at
            updated_at
          }
        }
      }
    }`);
}
//#endregion

//#region InventoryFilterData
export interface InventoryFilterDataResponse {
  inventoryFilterData: {
    code: string;
    success: boolean;
    message: string;
    filterData: InventoryFilterData;
  };
}

export interface InventoryFilterData {
  makesAndModels: MakeAndModels[];
  years: number[];
  colors: string[];
}

export interface MakeAndModels {
  make: string;
  models: string[];
}

@Injectable({
  providedIn: 'root',
})
export class InventoryFilterDataGQL extends Query<InventoryFilterDataResponse> {
  document = gql(`
    query inventoryFilterData ($accountUid: ID) {
      inventoryFilterData(accountUid: $accountUid) {
        code
        success
        message
        filterData {
          makesAndModels {
            make
            models
          }
          years
          colors
        }
      }
    }`);
}

//#region Make
export interface MakesResponse {
  makes: {
    code: string;
    success: boolean;
    message: string;
    makes: Make[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class MakesGQL extends Query<MakesResponse> {
  document = gql(`
    query makes($where: ModelMakeFilterInput, $limit: Int, $offset: Int) {
      makes(where: $where, limit: $limit, offset: $offset) {
        code
        message
        success
        makes {
          id
          name
        }
      }
    }`);
}
//#endregion

//#region Model
export interface ModelsResponse {
  models: {
    code: string;
    success: boolean;
    message: string;
    models: Model[];
  };
}

export interface ModelYearMakeFilter {
  years?: number[];
  make_ids?: number[];
}

export interface ModelsFromYearMakeQueryFilter {
  limit?: number;
  offset?: number;
  where?: ModelYearMakeFilter;
}

@Injectable({
  providedIn: 'root',
})
export class ModelsFromYearMakeGQL extends Query<ModelsResponse, ModelsFromYearMakeQueryFilter> {
  document = gql(`
    query modelsFromYearMake($where: ModelModelYearMakeInput, $limit: Int, $offset: Int) {
      modelsFromYearMake(where: $where, limit: $limit, offset: $offset) {
        code
        message
        success
        models {
          id
          year
          name
        }
      }
    }
  `);
}

export interface ModelYearsFromModel {
  modelYearsFromModel: {
    code: string;
    success: boolean;
    message: string;
    models: Model[];
  };
}

export interface ModelYearsFromModelQueryFilter {
  where: {
    model_ids: number[];
  };
}
@Injectable({
  providedIn: 'root',
})
export class ModelYearsFromModelGQL extends Query<ModelYearsFromModel, ModelYearsFromModelQueryFilter> {
  document = gql(`
    query modelYearsFromModel($where: ModelModelYearInput) {
      modelYearsFromModel(where: $where) {
        code
        message
        success
        models {
          id
          year
          name
        }
      }
    }
  `);
}
//#endregion
